import type { FC, ReactNode } from 'react';
import { Avatar, Col, Input, InputNumber, Row, Space } from 'antd';
import { TbArrowUpRight, TbArticle, TbForms, TbInfoCircle, TbPhoto, TbWorld } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import FontFamilyDropdown from '@/shared/components/dropdowns/FontFamilyDropdown';
import { PlatformForm as Form, SettingsToggleRow } from '@/shared/components/forms';
import { EnrichedTooltip } from '@/shared/components/tooltips';
import { defaultLightTokens } from '@/theme';
import type { WhitelabelSharedResourcesSettings } from '@/types';
import { corporateLogo } from '@/utilities';

import { toTokenString } from '../../configuration';
import WhitelabelColorInput from '../WhitelabelColorInput';
import WhitelabelFieldLabel from '../WhitelabelFieldLabel';
import WhitelabelSectionCard from '../WhitelabelSectionCard';

interface WhitelabelSharedResourcesTabProps {
    isFieldUpdated?: <
        TSection extends keyof WhitelabelSharedResourcesSettings,
        TField extends keyof WhitelabelSharedResourcesSettings[TSection],
    >(
        section: TSection,
        field: TField,
    ) => boolean;
    onFieldChange: <
        TSection extends keyof WhitelabelSharedResourcesSettings,
        TField extends keyof WhitelabelSharedResourcesSettings[TSection],
    >(
        section: TSection,
        field: TField,
        value: WhitelabelSharedResourcesSettings[TSection][TField],
    ) => void;
    onRevertField?: <
        TSection extends keyof WhitelabelSharedResourcesSettings,
        TField extends keyof WhitelabelSharedResourcesSettings[TSection],
    >(
        section: TSection,
        field: TField,
    ) => void;
    sharedResources: WhitelabelSharedResourcesSettings;
    workspaceName?: string;
}

const WhitelabelSharedResourcesTab: FC<WhitelabelSharedResourcesTabProps> = ({
    isFieldUpdated,
    onFieldChange,
    onRevertField,
    sharedResources,
    workspaceName,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const iconStyle = { color: token.colorTextTertiary, verticalAlign: '-0.15rem' } as const;
    const headerBackground =
        sharedResources.header.backgroundColor || toTokenString(defaultLightTokens.colorBgContainer, '#ffffff');
    const headerText = sharedResources.header.textColor || '#111827';
    const contentBackground =
        sharedResources.content.backgroundColor || toTokenString(defaultLightTokens.colorBgBase, '#fafafa');
    const contentText = sharedResources.content.textColor || '#111827';
    const linkColor = sharedResources.content.linkColor || toTokenString(defaultLightTokens.colorPrimary, '#1890ff');
    const borderColor = sharedResources.content.borderColor || toTokenString(defaultLightTokens.colorBorder, '#f2f2f2');
    const updatedLabel = <
        TSection extends keyof WhitelabelSharedResourcesSettings,
        TField extends keyof WhitelabelSharedResourcesSettings[TSection],
    >(
        label: string,
        section: TSection,
        field: TField,
    ) => (
        <WhitelabelFieldLabel
            label={label}
            onRevert={onRevertField ? () => onRevertField(section, field) : undefined}
            updated={Boolean(isFieldUpdated?.(section, field))}
        >
            {label}
        </WhitelabelFieldLabel>
    );
    const colorPreview = (backgroundColor: string, children: ReactNode, previewBorderColor = token.colorBorder) => (
        <div
            style={{
                alignItems: 'center',
                backgroundColor,
                border: `1px solid ${previewBorderColor}`,
                borderRadius: token.borderRadius,
                boxSizing: 'border-box',
                display: 'flex',
                justifyContent: 'center',
                minHeight: '5.5rem',
                minWidth: 0,
                padding: '0.75rem',
                width: '100%',
            }}
        >
            {children}
        </div>
    );
    const enrichedColorLabel = <
        TSection extends keyof WhitelabelSharedResourcesSettings,
        TField extends keyof WhitelabelSharedResourcesSettings[TSection],
    >(
        label: string,
        description: string,
        section: TSection,
        field: TField,
        visual: ReactNode,
    ) => (
        <WhitelabelFieldLabel
            label={label}
            onRevert={onRevertField ? () => onRevertField(section, field) : undefined}
            updated={Boolean(isFieldUpdated?.(section, field))}
        >
            {label}
            <EnrichedTooltip
                description={description}
                label={label}
                learnMoreTopic='shared-resource appearance'
                visual={visual}
            >
                <TbInfoCircle
                    aria-hidden='true'
                    size='0.875rem'
                    style={{ color: token.colorTextTertiary, marginLeft: '0.25rem' }}
                />
            </EnrichedTooltip>
        </WhitelabelFieldLabel>
    );

    return (
        <Space direction='vertical' size='middle' style={{ width: '100%' }}>
            <WhitelabelSectionCard
                description='Customize the identity and navigation shown at the top of every shared resource.'
                icon={<TbPhoto size='1rem' style={iconStyle} />}
                title='Header'
            >
                <Row gutter={[12, 12]}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label={updatedLabel('Brand Name', 'header', 'brandName')}
                            style={{ marginBottom: 0 }}
                        >
                            <Input
                                placeholder={workspaceName || 'Company Name...'}
                                value={sharedResources.header.brandName}
                                onChange={(event) => onFieldChange('header', 'brandName', event.target.value)}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item label={updatedLabel('Logo URL', 'header', 'logoUrl')} style={{ marginBottom: 0 }}>
                            <Input
                                placeholder='https://...'
                                prefix={<TbWorld size='1rem' style={{ color: token.colorTextTertiary }} />}
                                value={sharedResources.header.logoUrl}
                                onChange={(event) => onFieldChange('header', 'logoUrl', event.target.value)}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label={enrichedColorLabel(
                                'Header Background',
                                'Set the surface color behind the shared-resource identity and navigation.',
                                'header',
                                'backgroundColor',
                                colorPreview(
                                    headerBackground,
                                    <div style={{ color: headerText, width: '100%' }}>
                                        <strong style={{ display: 'block' }}>Quarterly Report</strong>
                                        <small>Workspace · Dashboard</small>
                                    </div>,
                                ),
                            )}
                            style={{ marginBottom: 0 }}
                        >
                            <WhitelabelColorInput
                                fallbackValue={toTokenString(defaultLightTokens.colorBgContainer, '#ffffff')}
                                placeholder={toTokenString(defaultLightTokens.colorBgContainer, '#ffffff')}
                                value={sharedResources.header.backgroundColor}
                                onChange={(value) => onFieldChange('header', 'backgroundColor', value)}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label={enrichedColorLabel(
                                'Header Text',
                                'Set the text color used for the shared-resource identity and navigation.',
                                'header',
                                'textColor',
                                colorPreview(
                                    headerBackground,
                                    <div style={{ color: headerText, width: '100%' }}>
                                        <strong style={{ display: 'block' }}>Quarterly Report</strong>
                                        <small>Workspace · Dashboard</small>
                                    </div>,
                                ),
                            )}
                            style={{ marginBottom: 0 }}
                        >
                            <WhitelabelColorInput
                                fallbackValue='#111827'
                                placeholder='#111827...'
                                value={sharedResources.header.textColor}
                                onChange={(value) => onFieldChange('header', 'textColor', value)}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <SettingsToggleRow
                            checked={Boolean(sharedResources.header.showWorkspaceName)}
                            description='Display the workspace name beside the shared-resource identity.'
                            divided
                            label={updatedLabel('Show Workspace Name', 'header', 'showWorkspaceName')}
                            switchAriaLabel='Show workspace name on shared resources'
                            onChange={(checked) => onFieldChange('header', 'showWorkspaceName', checked)}
                        />
                    </Col>
                    <Col span={24}>
                        <SettingsToggleRow
                            checked={Boolean(sharedResources.header.showResourceType)}
                            description='Identify whether the shared resource is a dashboard, report, memo, or another view.'
                            divided
                            label={updatedLabel('Show Resource Type', 'header', 'showResourceType')}
                            switchAriaLabel='Show resource type on shared resources'
                            onChange={(checked) => onFieldChange('header', 'showResourceType', checked)}
                        />
                    </Col>
                </Row>
            </WhitelabelSectionCard>

            <WhitelabelSectionCard
                description='Set the reading surface, typography, links, borders, and maximum width for shared content.'
                icon={<TbArticle size='1rem' style={iconStyle} />}
                title='Content'
            >
                <Row gutter={[12, 12]}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label={enrichedColorLabel(
                                'Content Background',
                                'Set the reading surface behind shared-resource content.',
                                'content',
                                'backgroundColor',
                                colorPreview(
                                    contentBackground,
                                    <div
                                        style={{
                                            backgroundColor: token.colorBgContainer,
                                            border: `1px solid ${borderColor}`,
                                            borderRadius: token.borderRadius,
                                            color: contentText,
                                            padding: '0.625rem',
                                            width: '85%',
                                        }}
                                    >
                                        Research summary
                                    </div>,
                                ),
                            )}
                            style={{ marginBottom: 0 }}
                        >
                            <WhitelabelColorInput
                                fallbackValue={toTokenString(defaultLightTokens.colorBgBase, '#fafafa')}
                                placeholder={toTokenString(defaultLightTokens.colorBgBase, '#fafafa')}
                                value={sharedResources.content.backgroundColor}
                                onChange={(value) => onFieldChange('content', 'backgroundColor', value)}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label={enrichedColorLabel(
                                'Content Text',
                                'Set the primary text color used throughout shared-resource content.',
                                'content',
                                'textColor',
                                colorPreview(
                                    contentBackground,
                                    <div style={{ color: contentText, width: '100%' }}>
                                        <strong style={{ display: 'block' }}>Investment thesis</strong>
                                        <span>Readable supporting analysis and context.</span>
                                    </div>,
                                    borderColor,
                                ),
                            )}
                            style={{ marginBottom: 0 }}
                        >
                            <WhitelabelColorInput
                                fallbackValue='#111827'
                                placeholder='#111827...'
                                value={sharedResources.content.textColor}
                                onChange={(value) => onFieldChange('content', 'textColor', value)}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label={enrichedColorLabel(
                                'Link Color',
                                'Set the accent color for links and navigational actions in shared resources.',
                                'content',
                                'linkColor',
                                colorPreview(
                                    contentBackground,
                                    <span style={{ color: linkColor, fontWeight: token.fontWeightStrong }}>
                                        Open supporting analysis
                                        <TbArrowUpRight
                                            aria-hidden='true'
                                            size='1rem'
                                            style={{ marginLeft: '0.25rem', verticalAlign: '-0.175rem' }}
                                        />
                                    </span>,
                                    borderColor,
                                ),
                            )}
                            style={{ marginBottom: 0 }}
                        >
                            <WhitelabelColorInput
                                fallbackValue={toTokenString(defaultLightTokens.colorPrimary, '#1890ff')}
                                placeholder={toTokenString(defaultLightTokens.colorPrimary, '#1890ff')}
                                value={sharedResources.content.linkColor}
                                onChange={(value) => onFieldChange('content', 'linkColor', value)}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label={enrichedColorLabel(
                                'Border Color',
                                'Set the border color around shared-resource controls and content panels.',
                                'content',
                                'borderColor',
                                colorPreview(
                                    contentBackground,
                                    <div
                                        style={{
                                            border: `1px solid ${borderColor}`,
                                            borderRadius: token.borderRadius,
                                            color: contentText,
                                            padding: '0.625rem',
                                            width: '85%',
                                        }}
                                    >
                                        Bordered content panel
                                    </div>,
                                    borderColor,
                                ),
                            )}
                            style={{ marginBottom: 0 }}
                        >
                            <WhitelabelColorInput
                                fallbackValue={toTokenString(defaultLightTokens.colorBorder, '#f2f2f2')}
                                placeholder={toTokenString(defaultLightTokens.colorBorder, '#f2f2f2')}
                                value={sharedResources.content.borderColor}
                                onChange={(value) => onFieldChange('content', 'borderColor', value)}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label={updatedLabel('Font Family', 'content', 'fontFamily')}
                            style={{ marginBottom: 0 }}
                        >
                            <FontFamilyDropdown
                                value={sharedResources.content.fontFamily}
                                onChange={(value) => onFieldChange('content', 'fontFamily', value)}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label={updatedLabel('Maximum Content Width', 'content', 'maxWidth')}
                            style={{ marginBottom: 0 }}
                        >
                            <InputNumber
                                addonAfter='px'
                                max={1920}
                                min={480}
                                value={sharedResources.content.maxWidth}
                                style={{ width: '100%' }}
                                onChange={(value) => onFieldChange('content', 'maxWidth', value ?? 960)}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </WhitelabelSectionCard>

            <WhitelabelSectionCard
                description='Add company links, support details, and attribution shown beneath shared resources.'
                icon={<TbForms size='1rem' style={iconStyle} />}
                title='Footer'
            >
                <Row gutter={[12, 12]}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label={updatedLabel('LinkedIn Page', 'footer', 'linkedin')}
                            style={{ marginBottom: 0 }}
                        >
                            <Input
                                addonBefore={
                                    <>
                                        <Avatar
                                            shape='square'
                                            size={16}
                                            src={corporateLogo('https://linkedin.com')}
                                            style={{ marginRight: '0.5rem', verticalAlign: '-0.175rem' }}
                                        />
                                        <span style={{ color: token.colorTextTertiary }}>linkedin.com/company/</span>
                                    </>
                                }
                                placeholder='countercyclical...'
                                value={sharedResources.footer.linkedin}
                                onChange={(event) => onFieldChange('footer', 'linkedin', event.target.value)}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item label={updatedLabel('X Profile', 'footer', 'twitter')} style={{ marginBottom: 0 }}>
                            <Input
                                addonBefore={
                                    <>
                                        <Avatar
                                            shape='square'
                                            size={16}
                                            src={corporateLogo('https://x.com')}
                                            style={{ marginRight: '0.5rem', verticalAlign: '-0.175rem' }}
                                        />
                                        <span style={{ color: token.colorTextTertiary }}>x.com/</span>
                                    </>
                                }
                                placeholder='countercyclicaI...'
                                value={sharedResources.footer.twitter}
                                onChange={(event) => onFieldChange('footer', 'twitter', event.target.value)}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label={updatedLabel('Support Email', 'footer', 'supportEmail')}
                            style={{ marginBottom: 0 }}
                        >
                            <Input
                                placeholder='support@company.com...'
                                value={sharedResources.footer.supportEmail}
                                onChange={(event) => onFieldChange('footer', 'supportEmail', event.target.value)}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            label={updatedLabel('Feedback Email', 'footer', 'feedbackEmail')}
                            style={{ marginBottom: 0 }}
                        >
                            <Input
                                placeholder='feedback@company.com...'
                                value={sharedResources.footer.feedbackEmail}
                                onChange={(event) => onFieldChange('footer', 'feedbackEmail', event.target.value)}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <SettingsToggleRow
                            checked={Boolean(sharedResources.footer.showPoweredBy)}
                            description='Show platform attribution at the bottom of publicly shared resources.'
                            divided
                            label={updatedLabel('Show Powered by Countercyclical', 'footer', 'showPoweredBy')}
                            switchAriaLabel='Show powered by Countercyclical'
                            onChange={(checked) => onFieldChange('footer', 'showPoweredBy', checked)}
                        />
                    </Col>
                </Row>
            </WhitelabelSectionCard>
        </Space>
    );
};

export default WhitelabelSharedResourcesTab;
